// GCP Compute Engine on-demand list prices for the GPU machine shapes that
// can run pool workers (g2 / g4 / n1+T4). Snapshot of the Cloud Billing
// catalog taken 2026-09-02 — pull again if numbers look stale.
//
// Per-resource unit prices below are USD/hr for compute & GPU SKUs, and
// USD/GB-month for disk SKUs. The compute model is identical to GCE's:
//   total = vCPU * vCpuPrice
//         + ramGb * ramPrice
//         + gpuCount * gpuPrice * gpuFraction   (fraction = 1 for full GPU)
//         + (windows ? vCPU * 0.046 : 0)
//         + (vws ? gpuCount * 0.20 : 0)
//         + diskGb * pdSsdPrice / 730
//         + localSsdGb * localSsdPrice / 730
//         + hyperdisk (capacity + IOPS and MBps above the free baselines) / 730
//
// The GPU rates are for the bare compute part. vWS is not a different GPU
// rate — it is the same rate plus one flat global licence SKU, so the vWS
// and standard SKUs of a shape differ by exactly $0.20/GPU/hr here. What
// they do NOT share is quota: see the note on `withVws` in PriceInputs.
//
// Sustained-use discounts (SUD) auto-apply at billing time and are NOT
// reflected here — these are list prices.

export const HOURS_PER_MONTH = 730;
export const WINDOWS_LICENSE_PER_VCPU_HR = 0.046;
export const VWS_LICENSE_PER_GPU_HR = 0.2;

/**
 * Hyperdisk data volume (STORY-126) — must stay in step with the constants in
 * GcpManagedBackend, which are what actually reach the instance template.
 */
export const HYPERDISK_DATA_GB = 375;
export const HYPERDISK_DATA_THROUGHPUT_MBPS = 1200;
export const HYPERDISK_DATA_IOPS = 4800;

/**
 * Provisioning included in the Hyperdisk Balanced capacity price. Only the
 * excess above these is billed, which is the whole reason a 375 GiB disk at
 * 1200 MBps comes to $81.40/mo in Iowa rather than $30 + everything:
 *   375 x $0.080 + (4800 - 3000) x $0.005 + (1200 - 140) x $0.040
 */
export const HYPERDISK_FREE_IOPS = 3000;
export const HYPERDISK_FREE_THROUGHPUT_MBPS = 140;

export interface RegionPricing {
  // G2 family (L4 bundled, sold separately on the bill)
  g2Core?: number; // $/vCPU/hr
  g2Ram?: number; // $/GB-RAM/hr
  l4Gpu?: number; // $/GPU/hr
  // G4 family (RTX PRO 6000 Blackwell, local SSD bundled $0)
  g4Core?: number;
  g4Ram?: number;
  rtxPro6000?: number; // $/GPU/hr, billed at fraction for MIG slices
  // N1 family (T4 attached separately; our pools pair it with the vWS part)
  n1Core?: number;
  n1Ram?: number;
  t4Gpu?: number; // $/GPU/hr (vWS license is a separate global SKU @ $0.20)
  // Persistent disks ($/GB-month)
  pdSsd?: number;
  pdBalanced?: number;
  pdStandard?: number;
  // Local SSD scratch ($/GB-month)
  localSsd?: number;
  // Hyperdisk Balanced, billed as three separate lines: capacity per GiB-month
  // plus provisioned IOPS and throughput per unit-month ABOVE the baselines
  // included in the capacity price (see HYPERDISK_FREE_*).
  hdCapacity?: number; // $/GiB-month
  hdIops?: number; // $/IOPS-month above the free baseline
  hdThroughput?: number; // $/MBps-month above the free baseline
}

// Regenerated 2026-09-02 straight from the Cloud Billing catalog
// (cloudbilling.googleapis.com services/6F81-5844-456A/skus), taking the
// OnDemand SKU per region and dropping the Spot / DWS / Commitment / Sole
// Tenancy variants. Every value that existed before this pass came back
// byte-identical, which is the check that the extraction is reading the same
// SKUs a human would.
//
// A family's rates are listed ONLY where that family is actually offered in
// the region, cross-checked against the machine-type and accelerator catalog
// rather than the SKU list alone — GCP publishes L4 and G2 pricing for
// australia-southeast1 and us-west2, where neither the g2 shapes nor the L4
// accelerator exist. Pricing something you cannot launch is the more
// misleading failure, so those are omitted and the matrix shows "—".
//
// The reverse case is real too: southamerica-east1 has G4 machine types and
// RTX PRO 6000 accelerators but GCP has published no São Paulo G4 SKU yet, so
// its G4 column is blank on purpose.
export const REGION_PRICING: Record<string, RegionPricing> = {
  // Americas (Iowa / Oregon / S. Carolina — Americas SKU group)
  'us-central1': {
    g2Core: 0.024988, g2Ram: 0.002927, l4Gpu: 0.56004,
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    n1Core: 0.031611, n1Ram: 0.004237, t4Gpu: 0.35,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
    hdCapacity: 0.08, hdIops: 0.005, hdThroughput: 0.04,
  },
  'us-east1': {
    g2Core: 0.024988, g2Ram: 0.002927, l4Gpu: 0.56004,
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    n1Core: 0.031611, n1Ram: 0.004237, t4Gpu: 0.35,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
    hdCapacity: 0.09, hdIops: 0.006, hdThroughput: 0.045,
  },
  'us-west1': {
    g2Core: 0.024988, g2Ram: 0.002927, l4Gpu: 0.56004,
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    n1Core: 0.031611, n1Ram: 0.004237, t4Gpu: 0.35,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
    hdCapacity: 0.08, hdIops: 0.005, hdThroughput: 0.04,
  },
  // Virginia — distinct SKU group from Americas
  'us-east4': {
    g2Core: 0.024906, g2Ram: 0.002918, l4Gpu: 0.558206,
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    n1Core: 0.035605, n1Ram: 0.004771, t4Gpu: 0.37,
    pdSsd: 0.187, pdBalanced: 0.11, pdStandard: 0.044, localSsd: 0.088,
    hdCapacity: 0.08, hdIops: 0.005, hdThroughput: 0.04,
  },
  // Las Vegas — G4 + G2 both live, and one of the 8-GPU RTX grants
  'us-west4': {
    g2Core: 0.028144, g2Ram: 0.003297, l4Gpu: 0.630773,
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
    n1Core: 0.035605, n1Ram: 0.004771, t4Gpu: 0.37,
    pdSsd: 0.187, pdBalanced: 0.11, pdStandard: 0.044, localSsd: 0.088,
    hdCapacity: 0.09, hdIops: 0.006, hdThroughput: 0.045,
  },
  // T4-only (no L4, no G4 hardware in the region)
  'us-west2': {
    n1Core: 0.03797, n1Ram: 0.005089, t4Gpu: 0.41,
    pdSsd: 0.204, pdBalanced: 0.12, pdStandard: 0.048, localSsd: 0.096,
    hdCapacity: 0.096, hdIops: 0.006, hdThroughput: 0.048,
  },
  // Columbus / Dallas / Salt Lake — G4 expansion regions, no G2
  'us-east5': {
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
    hdCapacity: 0.08, hdIops: 0.005, hdThroughput: 0.04,
  },
  'us-south1': {
    g4Core: 0.04891, g4Ram: 0.00587, rtxPro6000: 1.09565,
    pdSsd: 0.2006, pdBalanced: 0.12, pdStandard: 0.0472, localSsd: 0.0944,
    hdCapacity: 0.094, hdIops: 0.006, hdThroughput: 0.047,
  },
  'us-west3': {
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
    n1Core: 0.03797, n1Ram: 0.005089, t4Gpu: 0.41,
    pdSsd: 0.204, pdBalanced: 0.12, pdStandard: 0.048, localSsd: 0.096,
    hdCapacity: 0.096, hdIops: 0.006, hdThroughput: 0.048,
  },
  // Europe
  'europe-west1': {
    g2Core: 0.027515, g2Ram: 0.003223, l4Gpu: 0.61666,
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
    n1Core: 0.034773, n1Ram: 0.004661, t4Gpu: 0.35,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
    hdCapacity: 0.088, hdIops: 0.006, hdThroughput: 0.044,
  },
  'europe-west2': {
    g2Core: 0.028451, g2Ram: 0.003333, l4Gpu: 0.637654,
    g4Core: 0.058692, g4Ram: 0.007044, rtxPro6000: 1.31478,
    n1Core: 0.04073, n1Ram: 0.005458, t4Gpu: 0.41,
    pdSsd: 0.204, pdBalanced: 0.12, pdStandard: 0.048, localSsd: 0.096,
    hdCapacity: 0.091, hdIops: 0.006, hdThroughput: 0.046,
  },
  'europe-west3': {
    g2Core: 0.02945, g2Ram: 0.00345, l4Gpu: 0.660028,
    n1Core: 0.04073, n1Ram: 0.005458, t4Gpu: 0.41,
    pdSsd: 0.204, pdBalanced: 0.12, pdStandard: 0.048, localSsd: 0.096,
    hdCapacity: 0.094, hdIops: 0.006, hdThroughput: 0.047,
  },
  'europe-west4': {
    g2Core: 0.026264, g2Ram: 0.003077, l4Gpu: 0.58863,
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
    n1Core: 0.034802, n1Ram: 0.004664, t4Gpu: 0.35,
    pdSsd: 0.187, pdBalanced: 0.11, pdStandard: 0.044, localSsd: 0.088,
    hdCapacity: 0.084, hdIops: 0.005, hdThroughput: 0.042,
  },
  'europe-west6': {
    g2Core: 0.032736, g2Ram: 0.003835, l4Gpu: 0.78344,
    pdSsd: 0.221, pdBalanced: 0.13, pdStandard: 0.052, localSsd: 0.104,
    hdCapacity: 0.112, hdIops: 0.00655, hdThroughput: 0.0524,
  },
  'europe-west8': {
    g4Core: 0.058692, g4Ram: 0.007044, rtxPro6000: 1.31478,
    pdSsd: 0.1972, pdBalanced: 0.116, pdStandard: 0.0464, localSsd: 0.0928,
    hdCapacity: 0.093, hdIops: 0.006, hdThroughput: 0.046,
  },
  'europe-north1': {
    g4Core: 0.053801, g4Ram: 0.006457, rtxPro6000: 1.205215,
    pdSsd: 0.187, pdBalanced: 0.11, pdStandard: 0.044, localSsd: 0.088,
    hdCapacity: 0.088, hdIops: 0.006, hdThroughput: 0.044,
  },
  // Middle East
  'me-central2': {
    g2Core: 0.039981, g2Ram: 0.004684, l4Gpu: 0.896064,
    g4Core: 0.05869, g4Ram: 0.00704, rtxPro6000: 1.31478,
    pdSsd: 0.272, pdBalanced: 0.16, pdStandard: 0.064, localSsd: 0.128,
    hdCapacity: 0.128, hdIops: 0.008, hdThroughput: 0.064,
  },
  'me-west1': {
    n1Core: 0.034772, n1Ram: 0.004661, t4Gpu: 0.385,
    pdSsd: 0.187, pdBalanced: 0.11, pdStandard: 0.044, localSsd: 0.088,
    hdCapacity: 0.088, hdIops: 0.006, hdThroughput: 0.044,
  },
  // APAC
  'asia-east1': {
    g2Core: 0.028934, g2Ram: 0.00339, l4Gpu: 0.648471,
    g4Core: 0.063583, g4Ram: 0.007631, rtxPro6000: 1.424345,
    n1Core: 0.036602, n1Ram: 0.004906, t4Gpu: 0.35,
    pdSsd: 0.17, pdBalanced: 0.1, pdStandard: 0.04, localSsd: 0.08,
    hdCapacity: 0.093, hdIops: 0.006, hdThroughput: 0.046,
  },
  'asia-northeast1': {
    g2Core: 0.03209, g2Ram: 0.003759, l4Gpu: 0.719204,
    g4Core: 0.063583, g4Ram: 0.007631, rtxPro6000: 1.424345,
    n1Core: 0.040618, n1Ram: 0.005419, t4Gpu: 0.37,
    pdSsd: 0.221, pdBalanced: 0.13, pdStandard: 0.052, localSsd: 0.104,
    hdCapacity: 0.102, hdIops: 0.006, hdThroughput: 0.051,
  },
  'asia-northeast3': {
    g2Core: 0.03209, g2Ram: 0.003759, l4Gpu: 0.719204,
    n1Core: 0.040618, n1Ram: 0.005419, t4Gpu: 0.37,
    pdSsd: 0.221, pdBalanced: 0.13, pdStandard: 0.052, localSsd: 0.104,
    hdCapacity: 0.091, hdIops: 0.006, hdThroughput: 0.046,
  },
  'asia-southeast1': {
    g2Core: 0.030828, g2Ram: 0.003612, l4Gpu: 0.690922,
    g4Core: 0.058692, g4Ram: 0.007044, rtxPro6000: 1.31478,
    n1Core: 0.038999, n1Ram: 0.005226, t4Gpu: 0.37,
    pdSsd: 0.187, pdBalanced: 0.11, pdStandard: 0.044, localSsd: 0.088,
    hdCapacity: 0.098, hdIops: 0.006, hdThroughput: 0.049,
  },
  // Oceania
  'australia-southeast1': {
    n1Core: 0.044856, n1Ram: 0.006011, t4Gpu: 0.44,
    pdSsd: 0.23, pdBalanced: 0.135, pdStandard: 0.054, localSsd: 0.108,
    hdCapacity: 0.1, hdIops: 0.006, hdThroughput: 0.05,
  },
  // South America — G4 hardware is present but has no published SKU yet
  'southamerica-east1': {
    n1Core: 0.05018, n1Ram: 0.006725, t4Gpu: 0.48,
    pdSsd: 0.255, pdBalanced: 0.15, pdStandard: 0.06, localSsd: 0.12,
    hdCapacity: 0.127, hdIops: 0.008, hdThroughput: 0.064,
  },
};

export const REGION_LABELS: Record<string, string> = {
  'us-central1': 'US Central (Iowa)',
  'us-west1': 'US West (Oregon)',
  'us-west2': 'US West (Los Angeles)',
  'us-west3': 'US West (Salt Lake)',
  'us-west4': 'US West (Las Vegas)',
  'us-east1': 'US East (S. Carolina)',
  'us-east4': 'US East (N. Virginia)',
  'us-east5': 'US East (Columbus)',
  'us-south1': 'US South (Dallas)',
  'europe-west1': 'Europe (Belgium)',
  'europe-west2': 'Europe (London)',
  'europe-west3': 'Europe (Frankfurt)',
  'europe-west4': 'Europe (Netherlands)',
  'europe-west6': 'Europe (Zurich)',
  'europe-west8': 'Europe (Milan)',
  'europe-north1': 'Europe (Finland)',
  'me-central2': 'Middle East (Dammam)',
  'me-west1': 'Middle East (Tel Aviv)',
  'asia-east1': 'Asia (Taiwan)',
  'asia-northeast1': 'Asia (Tokyo)',
  'asia-northeast3': 'Asia (Seoul)',
  'asia-southeast1': 'Asia (Singapore)',
  'australia-southeast1': 'Australia (Sydney)',
  'southamerica-east1': 'South America (São Paulo)',
};

export type MachineFamily = 'g2' | 'g4' | 'n1';
export type GpuKind = 'l4' | 'rtx-pro-6000' | 't4';

export interface MachineSpec {
  name: string;
  family: MachineFamily;
  vcpus: number;
  ramGb: number;
  gpu: {
    kind: GpuKind;
    /** Logical GPU count (an integer; fractional MIG slices keep count=1
     *  and use `fraction` < 1 to represent the partition). */
    count: number;
    /** GPU-time multiplier for MIG-slice shapes. Undefined = full GPU. */
    fraction?: number;
    vramGb: number;
  };
  /** Local SSD that must be attached separately and billed per-GB. */
  attachedLocalSsdGb: number;
  /** Local SSD bundled into the shape price ($0 SKU). Display-only. */
  bundledLocalSsdGb?: number;
  /**
   * Hyperdisk Balanced data volume attached INSTEAD of Local SSD (STORY-126).
   * Mutually exclusive with attachedLocalSsdGb, and set on exactly the shapes
   * resolveDataVolume() gives a hyperdisk to — the single-card g2 sizes. If the
   * two ever disagree the console quietly misprices a node, so they are worth
   * changing together.
   */
  hyperdiskDataGb?: number;
}

// Shapes are the whole published G2 and G4 families, read off the Compute
// API on 2026-09-02 (`machine-types describe`), not the pricing page. The
// local-SSD figures are the per-shape counts GCP accepts × 375 GiB, and are
// kept in step with the table in GcpManagedBackend.createInstanceTemplateRaw
// — those counts are a set, not a range, so the two must not drift.
export const MACHINE_SPECS: MachineSpec[] = [
  // G2 — NVIDIA L4 (Ada Lovelace), 24 GB VRAM per card. -32 breaks the
  // pattern: 32 vCPUs but a single L4, where -24 already has two.
  //
  // The single-card sizes carry a Hyperdisk data volume rather than Local SSD
  // (STORY-126) — same 375 GiB, 3.1x the write throughput, and it survives a
  // reboot. The multi-card sizes keep their striped Local SSDs.
  { name: 'g2-standard-4',  family: 'g2', vcpus: 4,  ramGb: 16,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 0, hyperdiskDataGb: 375 },
  { name: 'g2-standard-8',  family: 'g2', vcpus: 8,  ramGb: 32,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 0, hyperdiskDataGb: 375 },
  { name: 'g2-standard-12', family: 'g2', vcpus: 12, ramGb: 48,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 0, hyperdiskDataGb: 375 },
  { name: 'g2-standard-16', family: 'g2', vcpus: 16, ramGb: 64,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 0, hyperdiskDataGb: 375 },
  { name: 'g2-standard-24', family: 'g2', vcpus: 24, ramGb: 96,
    gpu: { kind: 'l4', count: 2, vramGb: 48 }, attachedLocalSsdGb: 750 },
  { name: 'g2-standard-32', family: 'g2', vcpus: 32, ramGb: 128,
    gpu: { kind: 'l4', count: 1, vramGb: 24 }, attachedLocalSsdGb: 0, hyperdiskDataGb: 375 },
  { name: 'g2-standard-48', family: 'g2', vcpus: 48, ramGb: 192,
    gpu: { kind: 'l4', count: 4, vramGb: 96 }, attachedLocalSsdGb: 1500 },
  { name: 'g2-standard-96', family: 'g2', vcpus: 96, ramGb: 384,
    gpu: { kind: 'l4', count: 8, vramGb: 192 }, attachedLocalSsdGb: 3000 },

  // G4 — NVIDIA RTX PRO 6000 Blackwell, 96 GB VRAM per whole card. The
  // fractional shapes are vGPU slices of ONE physical GPU (GCP's own
  // description: "1/8 NVIDIA RTX PRO 6000 GPU"); -96 and up hold whole
  // extra cards. g4-standard-6 is the single g4 shape with no local SSD
  // at all — GCP accepts only [0] there.
  { name: 'g4-standard-6',  family: 'g4', vcpus: 6,  ramGb: 22,
    gpu: { kind: 'rtx-pro-6000', count: 1, fraction: 0.125, vramGb: 12 },
    attachedLocalSsdGb: 0 },
  { name: 'g4-standard-12', family: 'g4', vcpus: 12, ramGb: 45,
    gpu: { kind: 'rtx-pro-6000', count: 1, fraction: 0.25, vramGb: 24 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 375 },
  { name: 'g4-standard-24', family: 'g4', vcpus: 24, ramGb: 90,
    gpu: { kind: 'rtx-pro-6000', count: 1, fraction: 0.5, vramGb: 48 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 750 },
  { name: 'g4-standard-48', family: 'g4', vcpus: 48, ramGb: 180,
    gpu: { kind: 'rtx-pro-6000', count: 1, vramGb: 96 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 1500 },
  { name: 'g4-standard-96', family: 'g4', vcpus: 96, ramGb: 360,
    gpu: { kind: 'rtx-pro-6000', count: 2, vramGb: 192 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 3000 },
  { name: 'g4-standard-192', family: 'g4', vcpus: 192, ramGb: 720,
    gpu: { kind: 'rtx-pro-6000', count: 4, vramGb: 384 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 6000 },
  { name: 'g4-standard-384', family: 'g4', vcpus: 384, ramGb: 1440,
    gpu: { kind: 'rtx-pro-6000', count: 8, vramGb: 768 },
    attachedLocalSsdGb: 0, bundledLocalSsdGb: 12000 },

  // N1 + attached T4 vWS — Turing-era, kept for legacy parity
  { name: 'n1-standard-4',  family: 'n1', vcpus: 4,  ramGb: 15,
    gpu: { kind: 't4', count: 1, vramGb: 16 }, attachedLocalSsdGb: 375 },
  { name: 'n1-standard-8',  family: 'n1', vcpus: 8,  ramGb: 30,
    gpu: { kind: 't4', count: 1, vramGb: 16 }, attachedLocalSsdGb: 375 },
  { name: 'n1-standard-16', family: 'n1', vcpus: 16, ramGb: 60,
    gpu: { kind: 't4', count: 1, vramGb: 16 }, attachedLocalSsdGb: 375 },
];

export interface PriceBreakdown {
  /** Sum of vCPU + RAM line items, $/hr */
  compute: number;
  /** GPU line item (fraction-adjusted), $/hr */
  gpu: number;
  /** Windows Server 2025 Datacenter license, $/hr (0 if Linux) */
  windows: number;
  /** NVIDIA vWS license for T4-vWS, $/hr (0 unless vWS) */
  vws: number;
  /** Disk line items (boot pd-ssd + attached local SSD), $/hr */
  disks: number;
  total: number;
}

export interface PriceInputs {
  withWindows: boolean;
  bootDiskGb: number;
  /**
   * Charge the NVIDIA vWS licence. Every GPU we model (L4, RTX PRO 6000, T4)
   * ships in both a bare compute part and an RTX Virtual Workstation part;
   * picking vWS costs $0.20/GPU/hr on top of the GPU SKU and also draws on a
   * different regional quota. Our n1+T4 and g4 pools run vWS today, our g2
   * pools run bare — so no single default is right for the whole matrix, and
   * the page makes it a toggle.
   */
  withVws: boolean;
}

/** Returns null when the region doesn't list pricing for this shape's
 *  family — the caller should render a `—` in the matrix cell. */
export function computePrice(
  spec: MachineSpec,
  region: string,
  inputs: PriceInputs,
): PriceBreakdown | null {
  const rp = REGION_PRICING[region];
  if (!rp) return null;

  let compute = 0;
  let gpu = 0;

  if (spec.family === 'g2') {
    if (rp.g2Core == null || rp.g2Ram == null || rp.l4Gpu == null) return null;
    compute = spec.vcpus * rp.g2Core + spec.ramGb * rp.g2Ram;
    gpu = spec.gpu.count * rp.l4Gpu * (spec.gpu.fraction ?? 1);
  } else if (spec.family === 'g4') {
    if (rp.g4Core == null || rp.g4Ram == null || rp.rtxPro6000 == null) return null;
    compute = spec.vcpus * rp.g4Core + spec.ramGb * rp.g4Ram;
    gpu = spec.gpu.count * rp.rtxPro6000 * (spec.gpu.fraction ?? 1);
  } else {
    if (rp.n1Core == null || rp.n1Ram == null || rp.t4Gpu == null) return null;
    compute = spec.vcpus * rp.n1Core + spec.ramGb * rp.n1Ram;
    gpu = spec.gpu.count * rp.t4Gpu;
  }

  // One global licence SKU ("Licensing Fee for NVIDIA Quadro Virtual
  // Workstation"), $0.20 per GPU per hour, same price in every region and for
  // every GPU model. Charged on the card count, not the vGPU slice fraction —
  // a g4-standard-6 holds an eighth of a card but still occupies one, and GCP
  // publishes no per-slice licence SKU to say otherwise.
  const vws = inputs.withVws ? spec.gpu.count * VWS_LICENSE_PER_GPU_HR : 0;

  const windows = inputs.withWindows ? spec.vcpus * WINDOWS_LICENSE_PER_VCPU_HR : 0;

  let disks = 0;
  if (rp.pdSsd != null) {
    disks += (inputs.bootDiskGb * rp.pdSsd) / HOURS_PER_MONTH;
  }
  if (spec.attachedLocalSsdGb > 0 && rp.localSsd != null) {
    disks += (spec.attachedLocalSsdGb * rp.localSsd) / HOURS_PER_MONTH;
  }
  // Hyperdisk bills as three lines, and only the provisioning ABOVE the
  // included baselines counts — which is why this lands nearer the Local SSD
  // it replaces than the raw 1200 MBps figure suggests.
  if (
    spec.hyperdiskDataGb != null &&
    rp.hdCapacity != null &&
    rp.hdIops != null &&
    rp.hdThroughput != null
  ) {
    const monthly =
      spec.hyperdiskDataGb * rp.hdCapacity +
      Math.max(0, HYPERDISK_DATA_IOPS - HYPERDISK_FREE_IOPS) * rp.hdIops +
      Math.max(0, HYPERDISK_DATA_THROUGHPUT_MBPS - HYPERDISK_FREE_THROUGHPUT_MBPS) *
        rp.hdThroughput;
    disks += monthly / HOURS_PER_MONTH;
  }

  const total = compute + gpu + windows + vws + disks;
  return { compute, gpu, windows, vws, disks, total };
}

/** Indicative relative graphics+encode score vs. an L4 baseline (=1.0).
 *  These are coarse ratios derived from public benchmarks (3DMark / NVENC
 *  throughput) — useful for relative perf/$ ranking, not absolute claims. */
export function relativePerf(spec: MachineSpec): number {
  if (spec.gpu.kind === 'l4') return 1.0;
  if (spec.gpu.kind === 't4') return 0.33;
  // RTX PRO 6000 Blackwell — full GPU benchmarks ~3–4× an L4 for rasterized
  // workloads + 3× NVENC throughput. Slice proportionally for MIG shapes.
  const full = 3.5;
  return full * (spec.gpu.fraction ?? 1) * spec.gpu.count;
}
